import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ChevronLeft,
    Calendar,
    MapPin,
    Check,
    X,
    Clock,
    Sparkles,
    ArrowRight,
    Car,
    Users,
    Bus,
} from "lucide-react";
import { useData } from "../context/DataContext";
import { formatCurrency } from "../lib/format";
import SEO from "../components/SEO";
import useViewTracker from "../hooks/useViewTracker";

/* Pricing fallback — derive missing SUV/Tempo prices from the sedan/legacy price */
const resolvePrices = (pkg) => {
    const sedan = pkg.sedanPrice ?? pkg.price ?? 0;
    const suv = pkg.suvPrice ?? Math.round((sedan * 1.30) / 100) * 100;
    const tempo = pkg.tempoPrice ?? Math.round((sedan * 1.75) / 100) * 100;
    return { sedan, suv, tempo };
};

const VEHICLES = [
    { id: "sedan", label: "Sedan", sub: "4 + driver · luxury", icon: Car },
    { id: "suv", label: "SUV", sub: "6 + driver · Innova Crysta", icon: Users },
    { id: "tempo", label: "Tempo Van", sub: "12 + driver · large group", icon: Bus },
];

const PackageDetail = () => {
    const { pkgId } = useParams();
    const navigate = useNavigate();
    const { packages } = useData();
    const pkg = packages.find((p) => p.id === pkgId);
    const [vehicle, setVehicle] = useState("sedan");
    useViewTracker("package", pkgId);

    if (!pkg) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="text-center">
                    <p className="text-ink-light">Package not found.</p>
                    <button onClick={() => navigate("/tour-packages")} className="mt-4 text-saffron-600 font-semibold">Back to packages</button>
                </div>
            </div>
        );
    }

    const prices = resolvePrices(pkg);
    const selectedPrice = prices[vehicle];

    /* JSON-LD: BreadcrumbList + Tour Product */
    const breadcrumbJsonLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://southtrip.in/" },
            { "@type": "ListItem", position: 2, name: "Tour Packages", item: "https://southtrip.in/tour-packages" },
            { "@type": "ListItem", position: 3, name: pkg.route, item: `https://southtrip.in/tour-packages/${pkg.id}` },
        ],
    };
    const tourJsonLd = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: pkg.route,
        description: `${pkg.days}-day Kerala tour package from ${pkg.startingCity}. ${pkg.highlight || ""}`.trim(),
        image: pkg.image,
        brand: { "@type": "Brand", name: "SouthTrip.in by LuxSaCabs" },
        offers: {
            "@type": "Offer",
            priceCurrency: "INR",
            price: String(prices.sedan),
            availability: "https://schema.org/InStock",
            url: `https://southtrip.in/tour-packages/${pkg.id}`,
        },
    };

    return (
        <div className="bg-cream-100 min-h-screen pb-20" data-testid="package-detail-page">
            <SEO
                title={`${pkg.route} · Kerala Tour Package · SouthTrip.in`}
                description={`${pkg.days}D / ${pkg.nights}N Kerala tour package from ${pkg.startingCity}. Cab, driver and ${pkg.km} km included. Book direct with verified Kerala drivers.`}
                path={`/tour-packages/${pkg.id}`}
                image={pkg.image}
                jsonLd={[breadcrumbJsonLd, tourJsonLd]}
            />
            {/* Hero */}
            <section className="relative h-[42vh] min-h-[320px] overflow-hidden">
                <img src={pkg.image} alt={pkg.route} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" />
                <div className="absolute top-5 left-5">
                    <button
                        onClick={() => navigate(-1)}
                        className="px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-sm font-semibold flex items-center gap-1 hover:bg-white"
                    >
                        <ChevronLeft className="w-4 h-4" /> Back
                    </button>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-10 text-white max-w-7xl mx-auto">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className="bg-saffron-500 text-white px-3 py-1 rounded-full text-xs font-semibold">{pkg.days}D / {pkg.nights}N</span>
                        <span className="bg-white/20 backdrop-blur text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> From {pkg.startingCity}
                        </span>
                        {pkg.highlight && (
                            <span className="bg-teal-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                                <Sparkles className="w-3 h-3" /> {pkg.highlight}
                            </span>
                        )}
                    </div>
                    <h1 className="font-display text-3xl lg:text-5xl font-medium leading-tight">{pkg.route}</h1>
                    <p className="text-cream-100/80 text-sm mt-1">{pkg.km} km · cab + driver · 100 km/day</p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* MAIN */}
                <div className="lg:col-span-8 space-y-6">
                    <div className="bg-white border border-cream-200 rounded-2xl p-6">
                        <h2 className="font-display text-2xl text-ink flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-saffron-600" /> Day-by-day itinerary
                        </h2>
                        <div className="mt-5 space-y-5">
                            {pkg.itinerary.map((d, i) => (
                                <div key={i} className="relative pl-9" data-testid={`itinerary-day-${d.day}`}>
                                    <span className="absolute left-0 top-0 w-7 h-7 rounded-full bg-saffron-500 text-white text-xs font-bold flex items-center justify-center">
                                        {d.day}
                                    </span>
                                    {i !== pkg.itinerary.length - 1 && (
                                        <span className="absolute left-3.5 top-7 bottom-[-20px] w-px bg-cream-200" />
                                    )}
                                    <h3 className="font-semibold text-ink flex items-center gap-2">
                                        <Clock className="w-3.5 h-3.5 text-teal-600" /> Day {d.day} — {d.title}
                                    </h3>
                                    <p className="text-sm text-ink-light mt-1.5 leading-relaxed">{d.details}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="bg-white border border-cream-200 rounded-2xl p-6">
                            <h3 className="font-semibold text-teal-700 flex items-center gap-2"><Check className="w-4 h-4" /> Inclusions</h3>
                            <ul className="mt-3 space-y-2 text-sm text-ink-light">
                                {pkg.inclusions.map((i, k) => (
                                    <li key={k} className="flex items-start gap-2">
                                        <Check className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" />{i}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="bg-white border border-cream-200 rounded-2xl p-6">
                            <h3 className="font-semibold text-saffron-700 flex items-center gap-2"><X className="w-4 h-4" /> Exclusions</h3>
                            <ul className="mt-3 space-y-2 text-sm text-ink-light">
                                {pkg.exclusions.map((i, k) => (
                                    <li key={k} className="flex items-start gap-2">
                                        <X className="w-4 h-4 text-saffron-600 shrink-0 mt-0.5" />{i}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* PRICE PANEL */}
                <aside className="lg:col-span-4">
                    <div className="bg-white rounded-2xl border border-cream-200 p-6 sticky top-24">
                        <div className="text-xs uppercase tracking-wider text-ink-light font-semibold">Choose your vehicle</div>
                        <div className="mt-3 space-y-2" data-testid="pkg-vehicle-picker">
                            {VEHICLES.map((v) => {
                                const Icon = v.icon;
                                const active = vehicle === v.id;
                                return (
                                    <button
                                        key={v.id}
                                        type="button"
                                        onClick={() => setVehicle(v.id)}
                                        data-testid={`pkg-vehicle-${v.id}`}
                                        className={`w-full text-left rounded-xl border p-3 flex items-center gap-3 transition ${
                                            active
                                                ? "border-saffron-500 bg-saffron-50"
                                                : "border-cream-200 hover:border-saffron-300"
                                        }`}
                                    >
                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-saffron-500 text-white" : "bg-cream-100 text-ink"}`}>
                                            <Icon className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold text-ink leading-tight">{v.label}</div>
                                            <div className="text-[11px] text-ink-light">{v.sub}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-base font-display font-semibold text-saffron-600">
                                                {formatCurrency(prices[v.id])}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-5 pt-5 border-t border-cream-200">
                            <div className="text-xs uppercase tracking-wider text-ink-light font-semibold">Total · {VEHICLES.find((x) => x.id === vehicle).label}</div>
                            <div className="font-display text-4xl font-semibold text-saffron-600 mt-1" data-testid="pkg-selected-price">
                                {formatCurrency(selectedPrice)}
                            </div>
                            <div className="text-xs text-ink-light">cab + driver · {pkg.days} days</div>
                        </div>

                        <dl className="mt-5 space-y-2 text-sm pt-4 border-t border-cream-200">
                            <div className="flex justify-between"><dt className="text-ink-light">Duration</dt><dd className="font-medium text-ink">{pkg.days}D / {pkg.nights}N</dd></div>
                            <div className="flex justify-between"><dt className="text-ink-light">Distance</dt><dd className="font-medium text-ink">{pkg.km} km</dd></div>
                            <div className="flex justify-between"><dt className="text-ink-light">Starts</dt><dd className="font-medium text-ink">{pkg.startingCity}</dd></div>
                        </dl>

                        <Link
                            to={`/tour-packages/book/${pkg.id}?vehicle=${vehicle}`}
                            className="mt-5 w-full block text-center px-6 py-3.5 rounded-xl bg-saffron-500 hover:bg-saffron-600 text-white font-semibold transition shadow-md brand-shadow flex items-center justify-center gap-2"
                            data-testid="pkg-book-now"
                        >
                            Book this Package <ArrowRight className="w-4 h-4" />
                        </Link>
                        <div className="text-[11px] text-center text-ink-light mt-3">
                            ✓ Free cancellation up to 7 days before
                        </div>
                    </div>
                </aside>
            </section>
        </div>
    );
};

export default PackageDetail;
